/**
 * CSV writing for client-side table exports (EN-5146).
 *
 * Deliberately hand-rolled rather than pulling in a spreadsheet library: the
 * admin exports are flat rows of text, and the alternative (SheetJS, for
 * .xlsx) costs several hundred KB of bundle for formatting nothing here uses.
 * The server-side .xlsx exports that already exist stay as they are.
 */

/** Excel reads a UTF-8 file as Latin-1 unless it starts with a BOM. */
const BOM = "\ufeff";

/**
 * Leading characters that make Excel and Sheets treat a cell as a formula.
 * A value beginning with one of these is prefixed with an apostrophe, which
 * the spreadsheet strips on display but not on evaluation.
 *
 * `-` is handled separately: it leads both a formula and a negative number,
 * and mangling every negative number to protect against the former is worse
 * than the risk. Only a non-numeric value starting with `-` is escaped.
 */
const FORMULA_LEADERS = ["=", "+", "@", "\t", "\r"];

function neutralizeFormula(text: string): string {
  if (text.length === 0) return text;

  const startsFormula =
    FORMULA_LEADERS.includes(text[0]) ||
    (text[0] === "-" && Number.isNaN(Number(text)));

  return startsFormula ? `'${text}` : text;
}

/** One cell's text, before quoting. */
export function csvCellText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(csvCellText).join(", ");
  return String(value);
}

function quote(text: string): string {
  // Quote whenever the value could otherwise change the shape of the row, or
  // lose meaning: a separator, a newline, a quote, or padding a reader would
  // trim.
  const needsQuotes = /[",\r\n]/.test(text) || text.trim() !== text;
  return needsQuotes ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * A CSV document: a header row, then one row per record. CRLF line endings,
 * which is what RFC 4180 specifies and what Excel expects on every platform.
 */
export function toCsv(headers: string[], rows: unknown[][]): string {
  const line = (cells: unknown[]) =>
    cells.map((cell) => quote(neutralizeFormula(csvCellText(cell)))).join(",");

  return BOM + [headers, ...rows].map(line).join("\r\n");
}
